import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVerificationHistory } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';

export default function Verifications() {
  const { data: verifications, loading, error } = useVerificationHistory();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['verifications', 'common']);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('subtitle')}</p>
        </div>
        {hasRole('verifier') && (
          <button className="btn-primary" onClick={() => navigate('/verifier/verify')}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>fact_check</span>
            {t('verifyCredential')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadVerificationsFailed')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { label: t('stats.total'), value: String((Array.isArray(verifications) ? verifications : []).length) },
              {
                label: t('stats.successRate'),
                value: (Array.isArray(verifications) ? verifications : []).length > 0
                  ? Math.round(((Array.isArray(verifications) ? verifications : []).filter((v) => v.result === 'valid' || v.result === 'verified').length / (Array.isArray(verifications) ? verifications : []).length) * 100) + '%'
                  : '0%',
              },
              { label: t('stats.avgLatency'), value: t('stats.na') },
            ].map((stat) => (
              <div key={stat.label} className="stat-card">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-text-main">{stat.value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle bg-slate-50/50">
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.credentialType')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.issuer')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.holder')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.verifiedAt')}</th>
                  <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('table.result')}</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(verifications) ? verifications : []).map((v) => (
                  <tr key={v.id} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary" style={{ fontSize: 18 }}>description</span>
                        <span className="text-sm font-bold text-text-main">{v.credentialType || t('table.unknown')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-bold text-text-main">{v.issuerName || (v.issuerDid ? v.issuerDid.slice(0, 20) + '...' : v.issuer ? v.issuer.slice(0, 20) + '...' : '—')}</p>
                      <span className="font-mono text-[10px] text-text-faint" title={v.issuerDid || v.issuer || ''}>{v.issuerDid || v.issuer || ''}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-text-main">
                        {v.holder ?? v.holderDid ?? ''}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-xs text-text-muted">{v.verifiedAt ?? v.timestamp ?? ''}</span>
                    </td>
                    <td className="px-6 py-4">
                      {v.result === 'valid' || v.result === 'verified' ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100/50 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                          <span className="size-1.5 rounded-full bg-emerald-500"></span>
                          {t('results.valid')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-red-700">
                          <span className="size-1.5 rounded-full bg-red-500"></span>
                          {v.result === 'revoked' ? t('results.revoked') : v.result}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
